// ./src/pages/private/UsersPage/UsersPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { UsersTable, UserEditModal } from "@/components/features";
import { useUsers } from "@/hooks";
import { User, UserInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { ENTITIES } from "@/config/userRoles";

export const UsersPage: React.FC<PageProps> = (props: PageProps) => {
  const { hasRightTo, isAuthenticated } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ userId?: string }>();

  // Parse URL to determine which modal to show
  const userIdFromUrl = params.userId;
  const isEditMode = !!userIdFromUrl && location.pathname.endsWith("/edit");

  // Use the users hook for all data management
  const {
    users,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    isUpdating,
    removingUserId,
    list,
    loadMore,
    refresh,
    get,
    update,
    remove,
    lastError,
    clearError,
  } = useUsers({
    autoLoad: true,
    isAuthenticated,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [_, setEditedUserId] = useState<string | null>(null);

  const canCreate = hasRightTo(
    ENTITIES.USER_INVITATIONS,
    OPERATION_RIGHTS.CREATE
  );

  // Load user when in edit mode
  useEffect(() => {
    if (isEditMode && userIdFromUrl) {
      const loadUser = async () => {
        await get(userIdFromUrl);
      };
      loadUser();
    }
  }, [isEditMode, userIdFromUrl, get]);

  // Update editing user when we get data
  useEffect(() => {
    if (userIdFromUrl) {
      const user = users.find((u) => u.id === userIdFromUrl);
      if (user) {
        if (isEditMode) {
          setEditingUser(user);
        }
      }
    } else {
      setEditingUser(null);
    }
  }, [isEditMode, userIdFromUrl, users]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: any = { ...filters };
      if (value.trim()) {
        newFilters.keyword = value.trim();
        newFilters.searchIn = ["firstName", "lastName", "email", "username"];
      } else {
        delete newFilters.keyword;
        delete newFilters.searchIn;
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list]
  );

  // Handle invite user button click - navigate to invitations page
  const handleInviteUser = useCallback(() => {
    navigate("/account/users/invitations/add");
  }, [navigate]);

  // Handle edit user
  const handleEditUser = useCallback(
    (user: User) => {
      clearError();
      if (user.id) {
        navigate(`/account/users/${user.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle remove user button click
  const handleRemoveUserClick = useCallback((user: User) => {
    setUserToDelete(user);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (userToDelete?.id) {
      const success = await remove(userToDelete.id);
      if (success) {
        setUserToDelete(null);
        if (userIdFromUrl === userToDelete.id) {
          navigate("/account/users");
        }
      }
    }
  }, [userToDelete, remove, userIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setUserToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingUser(null);
    clearError();
    navigate("/account/users");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle user update
  const handleUpdateUser = useCallback(
    async (id: string, data: UserInput): Promise<User | null> => {
      const result = await update(id, data);
      if (result) {
        setEditedUserId(result.id || null);
        navigate("/account/users");
      }
      return result;
    },
    [update, navigate]
  );

  // Get user display name for delete confirmation
  const getUserDisplayName = (user: User | null): string => {
    if (!user) return "";
    const parts = [user.firstName, user.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    return user.email || user.username || "this user";
  };

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="invite-user"
                  iconName="UserPlus"
                  onClick={handleInviteUser}
                  disabled={loading || isUpdating || !canCreate}
                >
                  Invite User
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading || isUpdating || !!removingUserId}
                />,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search users..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Users table */}
        <UsersTable
          users={users}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          onLoadMore={loadMore}
          onEditUser={handleEditUser}
          onRemoveUser={handleRemoveUserClick}
          removingUserId={removingUserId}
          //editedItemId={editedUserId}
        />

        {/* Empty state when no users */}
        <EmptyState
          display={isEmpty && !loading}
          title="No users yet"
          description="Start by inviting your first user to the platform"
          actionIcon="UserPlus"
          actionLabel="Invite User"
          onAction={handleInviteUser}
        />
      </Stack>

      {/* User edit modal (drawer) */}
      <UserEditModal
        opened={isEditMode}
        onClose={handleEditModalClose}
        user={isEditMode ? editingUser : null}
        onUpdateUser={handleUpdateUser}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!userToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${getUserDisplayName(
          userToDelete
        )}"?`}
        details="This action cannot be undone. The user will be permanently removed from the system."
        confirmInProgress={removingUserId === userToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
